import React from 'react';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import {
  Select,
  InputBase,
  Tabs,
  Tab,
  Radio,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';

const NextButton = styled(Button)({
  'borderRadius': '8px',
  'padding': '12px',
  'display': 'flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'gap': '10px',
  'background':
    'linear-gradient(104deg, #4361EE 10.06%, #4895EF 88.31%, #4CC9F0 153.14%)',
  'color': 'var(--alternative-text-color)',
  'textAlign': 'center',
  'fontFamily': 'Inter',
  'fontSize': '14px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '24px',
  'textTransform': 'none',
  '&:disabled': {
    background: 'var(--border-color)',
    color: 'var(--alternative-text-color)',
  },
});
const BackButton = styled(Button)({
  'borderRadius': '8px',
  'border': '1px solid var(--card-text)',
  'padding': '12px',
  'display': 'flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'gap': '10px',
  'color': 'var(--alternative-text-color)',
  'textAlign': 'center',
  'fontFamily': 'Inter',
  'fontSize': '14px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '24px',
  'textTransform': 'none',
  '&:disabled': {
    color: 'var(--alternative-text-color)',
  },
});
const ExportButton = styled(Button)({
  'borderRadius': '8px',
  'border': '1px solid var(--alternative-text-color)',
  'padding': '12px',
  'display': 'flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'gap': '10px',
  'height': '36px',
  'color': 'var(--alternative-text-color)',
  'textAlign': 'center',
  'fontFamily': 'Inter',
  'fontSize': '14px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '24px',
  'textTransform': 'none',
  '&:disabled': {
    border: '1px solid var(--card-text)',
    color: 'var(--card-text)'
  },
});
const TicketButton = styled(Button)({
  border: '0px',
  padding: '12px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
  color: 'var(--alternative-text-color)',
  textAlign: 'center',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '24px',
  textTransform: 'none',
});

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'var(--border-color)',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--alternative-text-color)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '18px',
    maxWidth: '20%',
  },
  [`&.${tableCellClasses.body}`]: {
    color: 'var(--alternative-text-color)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    borderBottom: '1px solid var(--border-color)',
    textOverflow: 'ellipsis',
    maxWidth: '20%',
  },
}));

const HomePageStyledTableCell = styled(TableCell)({
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--alternative-text-color)',
  fontFamily: 'Inter',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '18px',
  textOverflow: 'ellipsis',
  height: '30px',
  padding: '14px 16px',
});
const StyledSelect = styled(Select)({
  input: {
    padding: '0px',
  },
  icon: {
    fill: 'var(--alternative-text-color)',
  },
});

const CustomSwitch = styled(Switch)(() => ({
  'width': 34,
  'height': 18,
  'padding': 0,
  '& .MuiSwitch-switchBase': {
    'padding': 1,
    '&.Mui-checked': {
      'transform': 'translateX(16px)',
      'color': 'white',
      '& .MuiSwitch-thumb:before': {
        color: '#0E8BFF',
        border: '6px solid #fff',
      },
      '& + .MuiSwitch-track': {
        background: '#4895EF',
        opacity: 1,
        border: 'none',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: 'var(--border-color)',
    opacity: 1,
  },
}));

const CustomLabel = styled(FormControlLabel)({
  color: 'var(--card-text)',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  gap: '8px',
  marginBottom: '0px',
});

const VaultCustomLabel = styled(FormControlLabel)({
  color: 'var(--alternative-text-color)',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  gap: '8px',
  marginBottom: '0px',
});
const StyledInput = styled(InputBase)({
  borderRadius: 4,
  // Follows the theme: the surface below is --bg-secondary, which is light on
  // the light theme, so a hardcoded white here made the text invisible there.
  color: 'var(--text-primary-color)',
  position: 'relative',
  backgroundColor: 'var(--bg-secondary)',
  fontSize: 16,
  width: '100%',
  padding: '10px 12px',
});
const StyledTabs = styled(Tabs)({
  color: 'var(--alternative-text-color)',
  textAlign: 'center',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '150%',
  borderBottom: '1px solid var(--Background-300, var(--bg-secondary))',
});
const StyledTab = styled(Tab)({
  'textTransform': 'none',
  'minWidth': '50px',
  'color': 'var(--alternative-text-color)',
  '&.Mui-selected': {
    color: 'var(--alternative-text-color)',
  },
});
const StyledRadio = styled(Radio)({
  root: {
    color: '#4895EF',
  },
  checked: {},
});
const StyledLabel = styled(FormControlLabel)({
  root: {
    color: 'var(--alternative-text-color)',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px',
  },
});
const LicenseButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  padding: '6px 12px',
  lineHeight: 1.5,
  backgroundColor: '#004F81',
  width: '150px',
});

const StyledCheckbox = styled(Checkbox)({
  marginRight: '10px',
  width: '16px',
  height: '16px',
  borderRadius: '4px',

  backgroundColor: 'var(--checkbox-bg)',
  border: '1.5px solid var(--checkbox-border)',

  '&:hover': {
    backgroundColor: 'var(--checkbox-hover-bg)',
  },

  '&.Mui-checked': {
    backgroundColor: 'var(--checkbox-checked-bg)',
    // border: '1.5px solid var(--checkbox-checked-border)',
    color: 'var(--checkbox-checkmark)',
  },

  '&.Mui-focusVisible': {
    outline: '2px solid var(--checkbox-focus-ring)',
    outlineOffset: '2px',
  },
});

const VaultClearButton = styled(Button)({
  border: 'none',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'var(--alternative-text-color)',
  textAlign: 'center',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '24px',
  textTransform: 'none',
});

const NoOutlineNoBackground = styled(Button)({
  'border': 'none',
  'padding': '6px',
  'display': 'flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'color': 'var(--alternative-text-color)',
  'textAlign': 'center',
  'fontFamily': 'Inter',
  'fontSize': '14px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '24px',
  'textTransform': 'none',
  '&:hover': {
    color: '#0E8BFF',
  },
});

const SideNavAccordion = styled(MuiAccordion)({
  'color': 'var(--card-text)',
  'background': 'transparent',
  'boxShadow': 'none',
  '&:before': {
    display: 'none',
  },
  '&$expanded': {
    margin: '0px',
  },
});

const SideNavAccordionSummary = styled((props: any) => (
  <MuiAccordionSummary {...props} />
))(() => ({
  'color': 'var(--card-text)',
  'fontFamily': 'Inter',
  'fontSize': '14px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '24px',
  'marginBottom': -1,
  'minHeight': 56,
  '&$expanded': {
    minHeight: 56,
  },
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    gap: '16px',
    marginLeft: '14px',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'var(--card-text)',
  },
}));

const SideNavAccordionDetails = styled(MuiAccordionDetails)({
  padding: '0px',
});

const ModuleContentTooltip = styled(Tooltip)({
  tooltip: {
    display: 'flex',
    padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 'var(--radius-md, 8px)',
    background: 'var(--Background-200, var(--bg-colors))',
    maxWidth: '400px',
  },
  arrow: {
    color: 'var(--Background-200, var(--bg-colors))'
  }
});

// `componentsProps` for a ModuleContentTooltip surface; `sx` adds to or
// overrides the shared surface.
const moduleTooltipProps = (sx: Record<string, unknown> = {}) => ({
  tooltip: {
    sx: {
      display: 'flex',
      padding: 'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
      flexDirection: 'column',
      alignItems: 'flex-start',
      borderRadius: 'var(--radius-md, 8px)',
      background: 'var(--Background-200, var(--bg-colors))',
      '& .MuiTooltip-arrow': {
        color: 'var(--Background-200, var(--bg-colors))',
      },
      ...sx,
    },
  },
});

export {
  StyledTabs,
  StyledTab,
  StyledInput,
  NextButton,
  BackButton,
  ExportButton,
  TicketButton,
  StyledTableCell,
  HomePageStyledTableCell,
  StyledSelect,
  CustomSwitch,
  CustomLabel,
  StyledLabel,
  StyledRadio,
  LicenseButton,
  StyledCheckbox,
  VaultClearButton,
  VaultCustomLabel,
  SideNavAccordion,
  SideNavAccordionDetails,
  SideNavAccordionSummary,
  NoOutlineNoBackground,
  ModuleContentTooltip,
  moduleTooltipProps,
};
